import { useState } from 'react';
import { Sword, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { fmtDelta, deltaClass, firstName } from '../../lib/play';

export function MatchLine({ m, userId, canDelete, onDeleted, compact }) {
  const [busy, setBusy] = useState(false);
  const aWon = m.score_a > m.score_b;
  const myDelta = userId ? m.rating_deltas?.[userId] : undefined;

  const del = async () => {
    if (!window.confirm('Delete this match? Ratings will be recomputed.')) return;
    setBusy(true);
    try {
      await api.delete(`/matches/${m.id}`);
      toast.success('Match removed');
      onDeleted?.();
      window.dispatchEvent(new Event('play:changed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    } finally {
      setBusy(false);
    }
  };

  const Team = ({ names, won }) => (
    <span className={won ? 'font-bold' : 'text-muted-foreground'}>{names.map(firstName).join(' & ')}</span>
  );

  return (
    <div className="py-2 flex items-center gap-2 text-sm" data-testid={`match-line-${m.id}`}>
      <div className="flex-1 min-w-0">
        <p className="truncate">
          <Team names={m.team_a_names || []} won={aWon} />
          <span className="mx-1.5 font-display font-black tabular-nums">{m.score_a}–{m.score_b}</span>
          <Team names={m.team_b_names || []} won={!aWon} />
          {m.giant_slayer && <Sword className="inline w-3.5 h-3.5 ml-1 text-violet-500" title="Giant Slayer" />}
        </p>
        {!compact && (
          <p className="text-xs text-muted-foreground truncate">
            {m.session_date || ''}{m.session_title ? ` · ${m.session_title}` : ''}{m.logged_by_name ? ` · by ${firstName(m.logged_by_name)}` : ''}
          </p>
        )}
      </div>
      {myDelta != null && <span className={`text-xs font-bold tabular-nums ${deltaClass(myDelta)}`}>{fmtDelta(myDelta)}</span>}
      {canDelete && (
        <Button size="icon" variant="ghost" className="h-7 w-7 rounded-full" disabled={busy} onClick={del} data-testid={`match-delete-${m.id}`}>
          <Trash2 className="w-3.5 h-3.5 text-rose-500" />
        </Button>
      )}
    </div>
  );
}

export default function RecentMatches({ matches = [], userId, canScore, isAdmin, onChanged }) {
  if (!matches.length) return null;
  return (
    <div className="rounded-2xl border bg-card px-4 pt-3 pb-1" data-testid="recent-matches">
      <h3 className="font-display text-sm font-bold uppercase tracking-[0.16em]">Recent matches</h3>
      <div className="divide-y divide-border/60">
        {matches.map((m) => (
          <MatchLine key={m.id} m={m} userId={userId} canDelete={isAdmin || (canScore && m.logged_by === userId)} onDeleted={onChanged} />
        ))}
      </div>
    </div>
  );
}
